import React, { useState, useEffect, useCallback } from 'react';
import {
  Abbreviation,
  fetchAbbreviations,
  createAbbreviation,
  updateAbbreviation,
  deleteAbbreviation,
} from '../services/abbreviationService';
import { downloadAbbreviationsPdf } from '../utils/downloadAbbreviationsPdf';

type Mode = 'add' | 'edit' | 'delete';

interface AbbreviationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create/update/delete (e.g. to refresh caches). */
  onChanged?: () => void;
}

/** Filters the list by abbreviation or expansion (case-insensitive). */
function filterAbbreviations(list: Abbreviation[], query: string): Abbreviation[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (item) =>
      item.abbreviation.toLowerCase().includes(q) || item.expansion.toLowerCase().includes(q),
  );
}

/**
 * Admin panel (MXO-L6 only) to Add, Edit, or Delete note abbreviations.
 * Edit/Delete use a filter-as-you-type combobox; Delete requires confirmation.
 */
const AbbreviationsModal: React.FC<AbbreviationsModalProps> = ({ isOpen, onClose, onChanged }) => {
  const [mode, setMode] = useState<Mode>('add');
  const [items, setItems] = useState<Abbreviation[]>([]);
  const [loadError, setLoadError] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

  // Add-form fields.
  const [newAbbr, setNewAbbr] = useState<string>('');
  const [newExpansion, setNewExpansion] = useState<string>('');
  const [newStandalone, setNewStandalone] = useState<boolean>(false);

  // Edit/Delete selection.
  const [filter, setFilter] = useState<string>('');
  const [selected, setSelected] = useState<Abbreviation | null>(null);
  const [editExpansion, setEditExpansion] = useState<string>('');
  const [editStandalone, setEditStandalone] = useState<boolean>(false);
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);

  const loadList = useCallback(async (): Promise<void> => {
    try {
      const list = await fetchAbbreviations();
      if (list === null) {
        setLoadError('Could not load the abbreviation list. Please try again.');
        return;
      }
      setLoadError('');
      setItems(list);
    } catch {
      setLoadError('Could not load the abbreviation list. Please try again.');
    }
  }, []);

  const resetSelection = useCallback((): void => {
    setFilter('');
    setSelected(null);
    setEditExpansion('');
    setEditStandalone(false);
    setConfirmingDelete(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setMode('add');
    setMessage(null);
    setNewAbbr('');
    setNewExpansion('');
    setNewStandalone(false);
    resetSelection();
    void loadList();
  }, [isOpen, loadList, resetSelection]);

  const switchMode = (next: Mode): void => {
    setMode(next);
    setMessage(null);
    resetSelection();
  };

  const selectItem = (item: Abbreviation): void => {
    setSelected(item);
    setFilter(item.abbreviation);
    setEditExpansion(item.expansion);
    setEditStandalone(item.standalone);
    setConfirmingDelete(false);
    setMessage(null);
  };

  /**
   * Runs a mutation, then refreshes the list, reports the outcome, and invokes
   * `onSuccess` when it succeeded. Self-contained error handling so callers can
   * fire it without their own try/catch.
   */
  const runMutation = async (
    action: () => Promise<{ ok: boolean; message?: string }>,
    successText: string,
    onSuccess?: () => void,
  ): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await action();
      if (!result.ok) {
        setMessage({ kind: 'error', text: result.message || 'The change could not be saved.' });
        return;
      }
      await loadList();
      onChanged?.();
      setMessage({ kind: 'success', text: successText });
      onSuccess?.();
    } catch {
      setMessage({ kind: 'error', text: 'The change could not be saved. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (): void => {
    const abbreviation = newAbbr.trim();
    const expansion = newExpansion.trim();
    if (!abbreviation || !expansion) return;
    void runMutation(
      () => createAbbreviation({ abbreviation, expansion, standalone: newStandalone }),
      `Added "${abbreviation}".`,
      () => {
        setNewAbbr('');
        setNewExpansion('');
        setNewStandalone(false);
      },
    );
  };

  const handleEditSave = (): void => {
    if (!selected) return;
    const expansion = editExpansion.trim();
    if (!expansion) return;
    void runMutation(
      () => updateAbbreviation(selected.abbreviation, { expansion, standalone: editStandalone }),
      `Updated "${selected.abbreviation}".`,
    );
  };

  const handleDelete = (): void => {
    if (!selected) return;
    const removed = selected.abbreviation;
    void runMutation(() => deleteAbbreviation(removed), `Deleted "${removed}".`, resetSelection);
  };

  if (!isOpen) return null;

  const filtered = filterAbbreviations(items, filter);

  return (
    <div
      className="modal-overlay active"
      role="dialog"
      aria-modal="true"
      aria-label="Manage Abbreviations"
    >
      <div className="modal abbrev-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Abbreviations</h2>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body-scroll">
          <div className="abbrev-mode-tabs">
            {(['add', 'edit', 'delete'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                className={`abbrev-mode-tab${mode === m ? ' active' : ''}`}
                onClick={() => switchMode(m)}
              >
                {m === 'add' ? 'Add' : m === 'edit' ? 'Edit' : 'Delete'}
              </button>
            ))}
          </div>

          {loadError && mode !== 'add' && (
            <div className="field-inline-error" role="alert">
              {loadError}
            </div>
          )}

          {mode === 'add' && (
            <div className="abbrev-form">
              <div className="barrier-popup-field">
                <label>Abbreviation *</label>
                <input
                  type="text"
                  className="field-input"
                  value={newAbbr}
                  onChange={(e) => setNewAbbr(e.target.value.toUpperCase())}
                  placeholder="e.g. HPT"
                />
              </div>
              <div className="barrier-popup-field">
                <label>Expands to *</label>
                <input
                  type="text"
                  className="field-input"
                  value={newExpansion}
                  onChange={(e) => setNewExpansion(e.target.value)}
                  placeholder="e.g. high pressure turbine"
                />
              </div>
              <label className="abbrev-standalone-row">
                <input
                  type="checkbox"
                  checked={newStandalone}
                  onChange={(e) => setNewStandalone(e.target.checked)}
                />
                Standalone only (single letter, applied last — e.g. L, R)
              </label>
            </div>
          )}

          {(mode === 'edit' || mode === 'delete') && (
            <div className="abbrev-form">
              <div className="barrier-popup-field">
                <label>Find abbreviation</label>
                <input
                  type="text"
                  className="field-input"
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setSelected(null);
                    setConfirmingDelete(false);
                  }}
                  placeholder="Type to filter…"
                />
              </div>
              {!selected && (
                <div className="abbrev-combo-list">
                  {filtered.length === 0 && <div className="abbrev-combo-empty">No matches</div>}
                  {filtered.map((item) => (
                    <button
                      key={item.abbreviation}
                      type="button"
                      className="abbrev-combo-item"
                      onClick={() => selectItem(item)}
                    >
                      <span className="abbrev-combo-key">{item.abbreviation}</span>
                      <span className="abbrev-combo-exp">{item.expansion}</span>
                    </button>
                  ))}
                </div>
              )}

              {selected && mode === 'edit' && (
                <>
                  <div className="barrier-popup-field">
                    <label>{selected.abbreviation} expands to *</label>
                    <input
                      type="text"
                      className="field-input"
                      value={editExpansion}
                      onChange={(e) => setEditExpansion(e.target.value)}
                    />
                  </div>
                  <label className="abbrev-standalone-row">
                    <input
                      type="checkbox"
                      checked={editStandalone}
                      onChange={(e) => setEditStandalone(e.target.checked)}
                    />
                    Standalone only (single letter, applied last)
                  </label>
                </>
              )}

              {selected && mode === 'delete' && (
                <div className="abbrev-delete-preview">
                  <div>
                    <strong>{selected.abbreviation}</strong> — {selected.expansion}
                  </div>
                  {selected.standalone && (
                    <div className="abbrev-standalone-note">Standalone (applied last)</div>
                  )}
                </div>
              )}
            </div>
          )}

          {message && (
            <div
              className={message.kind === 'error' ? 'field-inline-error' : 'abbrev-success'}
              role="alert"
            >
              {message.text}
            </div>
          )}
        </div>

        <div className="barrier-popup-footer">
          <button
            className="modal-btn modal-btn-download"
            type="button"
            onClick={() => downloadAbbreviationsPdf(items)}
            disabled={items.length === 0}
            title="Download the abbreviation list as a PDF"
          >
            Download PDF
          </button>
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onClose}>
            Close
          </button>
          {mode === 'add' && (
            <button
              className="modal-btn modal-btn-save"
              type="button"
              disabled={busy || !newAbbr.trim() || !newExpansion.trim()}
              onClick={handleAdd}
            >
              Create
            </button>
          )}
          {mode === 'edit' && (
            <button
              className="modal-btn modal-btn-save"
              type="button"
              disabled={busy || !selected || !editExpansion.trim()}
              onClick={handleEditSave}
            >
              Save
            </button>
          )}
          {mode === 'delete' && (
            <button
              className="modal-btn modal-btn-danger"
              type="button"
              disabled={busy || !selected}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {confirmingDelete && selected && (
        <div
          className="modal-overlay active"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm delete"
        >
          <div className="modal abbrev-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete abbreviation?</h2>
            </div>
            <div className="modal-body-scroll">
              <p>
                Delete <strong>{selected.abbreviation}</strong> ({selected.expansion})? This cannot
                be undone.
              </p>
            </div>
            <div className="barrier-popup-footer">
              <button
                className="modal-btn modal-btn-cancel"
                type="button"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn modal-btn-danger"
                type="button"
                disabled={busy}
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbbreviationsModal;
